import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, FLYTO_DEFAULT_ZOOM } from '@lib/constants'
import { DEFAULT_MAP_STYLE_ID, type MapStyleId } from '@lib/mapStyles'

interface ViewState {
  longitude: number
  latitude: number
  zoom: number
  pitch?: number
  bearing?: number
}

interface BoundsBox {
  minLng: number
  maxLng: number
  minLat: number
  maxLat: number
}

interface MapState {
  viewState: ViewState
  selectedLocationId: string | null
  activeLayers: string[]
  isMapReady: boolean
  /** Currently selected map style ID */
  selectedMapStyleId: MapStyleId

  setViewState: (viewState: ViewState) => void
  flyTo: (lng: number, lat: number, zoom?: number, duration?: number, latitudeOffset?: number) => void
  fitBounds: (bounds: BoundsBox, padding?: number) => void
  selectLocation: (id: string | null) => void
  toggleLayer: (layerId: string) => void
  setMapReady: (ready: boolean) => void
  pendingFlyTo: { longitude: number; latitude: number; zoom: number; duration: number } | null
  clearPendingFlyTo: () => void
  /** Set the selected map style ID */
  setSelectedMapStyleId: (styleId: MapStyleId) => void

  searchMarker: { longitude: number; latitude: number } | null
  setSearchMarker: (marker: { longitude: number; latitude: number } | null) => void
}

/**
 * Calculate appropriate zoom level for given bounds
 * Based on Mapbox's bounding box to zoom calculation
 */
function calculateZoomForBounds(bounds: BoundsBox, padding: number = 50): number {
  const lngDiff = bounds.maxLng - bounds.minLng
  const latDiff = bounds.maxLat - bounds.minLat

  // If all points are at same location, use high zoom
  if (lngDiff === 0 && latDiff === 0) {
    return 12
  }

  // Calculate zoom based on the largest dimension
  // These values are approximations for common screen sizes
  const maxDiff = Math.max(lngDiff, latDiff)

  let zoom: number
  if (maxDiff > 20) zoom = 6
  // else if (maxDiff > 10) zoom = 4
  // else if (maxDiff > 5) zoom = 5
  // else if (maxDiff > 2) zoom = 6
  else if (maxDiff > 1) zoom = 7
  else if (maxDiff > 0.5) zoom = 8
  else if (maxDiff > 0.2) zoom = 9
  else if (maxDiff > 0.1) zoom = 10
  else if (maxDiff > 0.05) zoom = 11
  else zoom = 12

  // Apply padding reduction
  return Math.max(zoom - (padding > 50 ? 1 : 0), 3)
}

export const useMapStore = create<MapState>()(
  devtools(
    persist(
      (set) => ({
        viewState: {
          longitude: DEFAULT_MAP_CENTER[0],
          latitude: DEFAULT_MAP_CENTER[1],
          zoom: DEFAULT_MAP_ZOOM,
        },
        selectedLocationId: null,
        activeLayers: [],
        isMapReady: false,
        selectedMapStyleId: DEFAULT_MAP_STYLE_ID,

        setViewState: (viewState) => set({ viewState }),

        setSelectedMapStyleId: (styleId) => set({ selectedMapStyleId: styleId }),

        fitBounds: (bounds, padding = 50) => {
          // Calculate center
          const centerLng = (bounds.minLng + bounds.maxLng) / 2
          const centerLat = (bounds.minLat + bounds.maxLat) / 2

          // Calculate appropriate zoom
          const zoom = calculateZoomForBounds(bounds, padding)

          set({
            viewState: {
              longitude: centerLng,
              latitude: centerLat,
              zoom,
            },
          })
        },

        selectLocation: (id) => set({ selectedLocationId: id }),

        toggleLayer: (layerId) =>
          set((state) => ({
            activeLayers: state.activeLayers.includes(layerId)
              ? state.activeLayers.filter((id) => id !== layerId)
              : [...state.activeLayers, layerId],
          })),

        setMapReady: (isMapReady) => set({ isMapReady }),

        searchMarker: null,
        setSearchMarker: (marker) => set({ searchMarker: marker }),

        pendingFlyTo: null,
        flyTo: (longitude, latitude, zoom = FLYTO_DEFAULT_ZOOM, duration = 2000, latitudeOffset = 0) =>
          set({
            pendingFlyTo: { longitude, latitude: latitude + latitudeOffset, zoom, duration },
          }),
        clearPendingFlyTo: () => set({ pendingFlyTo: null }),
      }),
      {
        name: 'map-store',
        // Only persist the map style preference, not view state or other volatile data
        partialize: (state) => ({ selectedMapStyleId: state.selectedMapStyleId }),
      }
    ),
    { name: 'map-store' }
  )
)

